import { __extends } from "tslib";
import { Transform, TransformEvent, MathConsts, Matrix3D, Vector3D, AssetBase, } from '@awayjs/core';
import { BlendMode } from '@awayjs/stage';
import { BoundingVolumeType, HeirarchicalEvent, HierarchicalProperty, ContainerEvent, AlignmentMode, OrientationMode, BasicPartition } from '@awayjs/view';
import { StyleEvent, RenderableEvent, ElementsType, } from '@awayjs/renderer';
import { FocusEvent } from '../events/FocusEvent';
import { PrimitiveCubePrefab } from '../prefabs/PrimitiveCubePrefab';
import { PrimitiveSpherePrefab } from '../prefabs/PrimitiveSpherePrefab';
import { Sprite } from './Sprite';
import { Settings } from '../Settings';
/**
 * The DisplayObject class is the base class for all objects that can be
 * placed on the display list. The display list manages all objects displayed
 * in flash. Use the DisplayObjectContainer class to arrange the
 * display objects in the display list. DisplayObjectContainer objects can
 * have child display objects, while other display objects, such as Shape and
 * TextField objects, are "leaf" nodes that have only parents and siblings, no
 * children.
 *
 * <p>The DisplayObject class supports fbasic functionality like the <i>x</i>
 * and <i>y</i> position of an object, as well as more advanced properties of
 * the object such as its transformation matrix. </p>
 *
 * <p>DisplayObject is an abstract base class; therefore, you cannot call
 * DisplayObject directly. Invoking <code>new DisplayObject()</code> throws an
 * <code>ArgumentError</code> exception. </p>
 *
 * <p>All display objects inherit from the DisplayObject class.</p>
 *
 * <p>The DisplayObject class itself does not include any APIs for rendering
 * content onscreen. For that reason, if you want create a custom subclass of
 * the DisplayObject class, you will want to extend one of its subclasses that
 * do have APIs for rendering content onscreen, such as the Shape, Sprite,
 * Bitmap, SimpleButton, TextField, or MovieClip class.</p>
 *
 * <p>The DisplayObject class contains several broadcast events. Normally, the
 * target of any particular event is a specific DisplayObject instance. For
 * example, the target of an <code>added</code> event is the specific
 * DisplayObject instance that was added to the display list. Having a single
 * target restricts the placement of event listeners to that target and in
 * some cases the target's ancestors on the display list. With broadcast
 * events, however, the target is not a specific DisplayObject instance, but
 * rather all DisplayObject instances, including those that are not on the
 * display list. This means that you can add a listener to any DisplayObject
 * instance to listen for broadcast events. In addition to the broadcast
 * events listed in the DisplayObject class's Events table, the DisplayObject
 * class also inherits two broadcast events from the EventDispatcher class:
 * <code>activate</code> and <code>deactivate</code>.</p>
 *
 * <p>Some properties previously used in the ActionScript 1.0 and 2.0
 * MovieClip, TextField, and Button classes(such as <code>_alpha</code>,
 * <code>_height</code>, <code>_pName</code>, <code>_width</code>,
 * <code>_x</code>, <code>_y</code>, and others) have equivalents in the
 * ActionScript 3.0 DisplayObject class that are renamed so that they no
 * longer begin with the underscore(_) character.</p>
 *
 * <p>For more information, see the "Display Programming" chapter of the
 * <i>ActionScript 3.0 Developer's Guide</i>.</p>
 *
 * @event added            Dispatched when a display object is added to the
 *                         display list. The following methods trigger this
 *                         event:
 *                         <code>DisplayObjectContainer.addChild()</code>,
 *                         <code>DisplayObjectContainer.addChildAt()</code>.
 * @event addedToScene     Dispatched when a display object is added to the on
 *                         scene display list, either directly or through the
 *                         addition of a sub tree in which the display object
 *                         is contained. The following methods trigger this
 *                         event:
 *                         <code>DisplayObjectContainer.addChild()</code>,
 *                         <code>DisplayObjectContainer.addChildAt()</code>.
 * @event enterFrame       [broadcast event] Dispatched when the playhead is
 *                         entering a new frame. If the playhead is not
 *                         moving, or if there is only one frame, this event
 *                         is dispatched continuously in conjunction with the
 *                         frame rate. This event is a broadcast event, which
 *                         means that it is dispatched by all display objects
 *                         with a listener registered for this event.
 * @event exitFrame        [broadcast event] Dispatched when the playhead is
 *                         exiting the current frame. All frame scripts have
 *                         been run. If the playhead is not moving, or if
 *                         there is only one frame, this event is dispatched
 *                         continuously in conjunction with the frame rate.
 *                         This event is a broadcast event, which means that
 *                         it is dispatched by all display objects with a
 *                         listener registered for this event.
 * @event frameConstructed [broadcast event] Dispatched after the constructors
 *                         of frame display objects have run but before frame
 *                         scripts have run. If the playhead is not moving, or
 *                         if there is only one frame, this event is
 *                         dispatched continuously in conjunction with the
 *                         frame rate. This event is a broadcast event, which
 *                         means that it is dispatched by all display objects
 *                         with a listener registered for this event.
 * @event removed          Dispatched when a display object is about to be
 *                         removed from the display list. Two methods of the
 *                         DisplayObjectContainer class generate this event:
 *                         <code>removeChild()</code> and
 *                         <code>removeChildAt()</code>.
 *
 *                         <p>The following methods of a
 *                         DisplayObjectContainer object also generate this
 *                         event if an object must be removed to make room for
 *                         the new object: <code>addChild()</code>,
 *                         <code>addChildAt()</code>, and
 *                         <code>setChildIndex()</code>. </p>
 * @event removedFromScene Dispatched when a display object is about to be
 *                         removed from the display list, either directly or
 *                         through the removal of a sub tree in which the
 *                         display object is contained. Two methods of the
 *                         DisplayObjectContainer class generate this event:
 *                         <code>removeChild()</code> and
 *                         <code>removeChildAt()</code>.
 *
 *                         <p>The following methods of a
 *                         DisplayObjectContainer object also generate this
 *                         event if an object must be removed to make room for
 *                         the new object: <code>addChild()</code>,
 *                         <code>addChildAt()</code>, and
 *                         <code>setChildIndex()</code>. </p>
 * @event render           [broadcast event] Dispatched when the display list
 *                         is about to be updated and rendered. This event
 *                         provides the last opportunity for objects listening
 *                         for this event to make changes before the display
 *                         list is rendered. You must call the
 *                         <code>invalidate()</code> method of the Scene
 *                         object each time you want a <code>render</code>
 *                         event to be dispatched. <code>Render</code> events
 *                         are dispatched to an object only if there is mutual
 *                         trust between it and the object that called
 *                         <code>Scene.invalidate()</code>. This event is a
 *                         broadcast event, which means that it is dispatched
 *                         by all display objects with a listener registered
 *                         for this event.
 *
 *                         <p><b>Note: </b>This event is not dispatched if the
 *                         display is not rendering. This is the case when the
 *                         content is either minimized or obscured. </p>
 */
var DisplayObject = /** @class */ (function (_super) {
    __extends(DisplayObject, _super);
    /**
     * Creates a new <code>DisplayObject</code> instance.
     */
    function DisplayObject() {
        var _this = _super.call(this) || this;
        _this._sessionID = -1;
        _this._avmDepthID = -1;
        //public _implicitPartition: PartitionBase;
        _this._alignmentMode = AlignmentMode.REGISTRATION_POINT;
        _this._visible = true;
        _this._maskId = -1;
        _this._mouseEnabled = true;
        _this._maskMode = false;
        _this.isSlice9ScaledMC = false;
        _this.isSlice9ScaledSprite = false;
        _this.isAVMScene = false;
        // this is needed for AVM1 - todo: maybe do this on adapters ?
        _this.placeObjectTag = null;
        /**
         * A value from the BlendMode class that specifies which blend mode to use. A
         * bitmap can be drawn internally in two ways. If you have a blend mode
         * enabled or an external clipping mask, the bitmap is drawn by adding a
         * bitmap-filled square shape to the vector render. If you attempt to set
         * this property to an invalid value, Flash runtimes set the value to
         * <code>BlendMode.NORMAL</code>.
         *
         * <p>The <code>blendMode</code> property affects each pixel of the display
         * object. Each pixel is composed of three constituent colors(red, green,
         * and blue), and each constituent color has a value between 0x00 and 0xFF.
         * Flash Player or Adobe AIR compares each constituent color of one pixel in
         * the movie clip with the corresponding color of the pixel in the
         * background. For example, if <code>blendMode</code> is set to
         * <code>BlendMode.LIGHTEN</code>, Flash Player or Adobe AIR compares the red
         * value of the display object with the red value of the background, and uses
         * the lighter of the two as the value for the red component of the displayed
         * color.</p>
         *
         * <p>The following table describes the <code>blendMode</code> settings. The
         * BlendMode class defines string values you can use. The illustrations in
         * the table show <code>blendMode</code> values applied to a circular display
         * object(2) superimposed on another display object(1).</p>
         */
        _this._blendMode = '';
        /**
         *
         */
        _this.castsShadows = true;
        /**
         * An indexed array that contains each filter object currently associated
         * with the display object. The flash.filters package contains several
         * classes that define specific filters you can use.
         *
         * <p>Filters can be applied in Flash Professional at design time, or at run
         * time by using ActionScript code. To apply a filter by using ActionScript,
         * you must make a temporary copy of the entire <code>filters</code> array,
         * modify the temporary array, then assign the value of the temporary array
         * back to the <code>filters</code> array. You cannot directly add a new
         * filter object to the <code>filters</code> array.</p>
         *
         * <p>To add a filter by using ActionScript, perform the following steps
         * (assume that the target display object is named
         * <code>myDisplayObject</code>):</p>
         *
         * <ol>
         *   <li>Create a new filter object by using the constructor method of your
         * chosen filter class.</li>
         *   <li>Assign the value of the <code>myDisplayObject.filters</code> array
         * to a temporary array, such as one named <code>myFilters</code>.</li>
         *   <li>Add the new filter object to the <code>myFilters</code> temporary
         * array.</li>
         *   <li>Assign the value of the temporary array to the
         * <code>myDisplayObject.filters</code> array.</li>
         * </ol>
         *
         * <p>If the <code>filters</code> array is undefined, you do not need to use
         * a temporary array. Instead, you can directly assign an array literal that
         * contains one or more filter objects that you create. The first example in
         * the Examples section adds a drop shadow filter by using code that handles
         * both defined and undefined <code>filters</code> arrays.</p>
         *
         * <p>To modify an existing filter object, you must use the technique of
         * modifying a copy of the <code>filters</code> array:</p>
         *
         * <ol>
         *   <li>Assign the value of the <code>filters</code> array to a temporary
         * array, such as one named <code>myFilters</code>.</li>
         *   <li>Modify the property by using the temporary array,
         * <code>myFilters</code>. For example, to set the quality property of the
         * first filter in the array, you could use the following code:
         * <code>myFilters[0].quality = 1;</code></li>
         *   <li>Assign the value of the temporary array to the <code>filters</code>
         * array.</li>
         * </ol>
         *
         * <p>At load time, if a display object has an associated filter, it is
         * marked to cache itself as a transparent bitmap. From this point forward,
         * as long as the display object has a valid filter list, the player caches
         * the display object as a bitmap. This source bitmap is used as a source
         * image for the filter effects. Each display object usually has two bitmaps:
         * one with the original unfiltered source display object and another for the
         * final image after filtering. The final image is used when rendering. As
         * long as the display object does not change, the final image does not need
         * updating.</p>
         *
         * <p>The flash.filters package includes classes for filters. For example, to
         * create a DropShadow filter, you would write:</p>
         *
         * @throws ArgumentError When <code>filters</code> includes a ShaderFilter
         *                       and the shader output type is not compatible with
         *                       this operation(the shader must specify a
         *                       <code>pixel4</code> output).
         * @throws ArgumentError When <code>filters</code> includes a ShaderFilter
         *                       and the shader doesn't specify any image input or
         *                       the first input is not an <code>image4</code> input.
         * @throws ArgumentError When <code>filters</code> includes a ShaderFilter
         *                       and the shader specifies an image input that isn't
         *                       provided.
         * @throws ArgumentError When <code>filters</code> includes a ShaderFilter, a
         *                       ByteArray or Vector.<Number> instance as a shader
         *                       input, and the <code>width</code> and
         *                       <code>height</code> properties aren't specified for
         *                       the ShaderInput object, or the specified values
         *                       don't match the amount of data in the input data.
         *                       See the <code>ShaderInput.input</code> property for
         *                       more information.
         */
        _this._filters = null;
        /**
         *
         */
        _this.orientationMode = OrientationMode.DEFAULT;
        /**
         *
         */
        _this.zOffset = 0;
        _this.hasDispatchedAddedToStage = false;
        _this.cursorType = '';
        _this._isInFocus = false;
        _this._tabEnabled = false;
        _this._tabIndex = -1;
        _this._avmDepthID = -1;
        //global debug bounding boxes:
        //this._boundsVisible=true;
        _this._onInvalidatePropertiesDelegate = function (event) { return _this._onInvalidateProperties(event); };
        //creation of associated transform object
        _this._transform = new Transform(null);
        //setup transform listeners
        _this._transform.addEventListener(TransformEvent.INVALIDATE_MATRIX3D, function (_event) { return _this._invalidateHierarchicalProperty(HierarchicalProperty.SCENE_TRANSFORM); });
        _this._transform.addEventListener(TransformEvent.INVALIDATE_COLOR_TRANSFORM, function (_event) { return _this._invalidateHierarchicalProperty(HierarchicalProperty.COLOR_TRANSFORM); });
        //bounding volume dictonary
        //default bounds type
        _this._defaultBoundingVolume = _this._getDefaultBoundingVolume();
        return _this;
    }
    DisplayObject.prototype.getScriptPrecedence = function () {
        if (!this._parent) {
            return [];
        }
        var result = this._parent.getScriptPrecedence();
        if (this.placeObjectTag) {
            result.push(this.placeObjectTag.actionBlocksPrecedence);
        }
        return result;
    };
    DisplayObject.prototype.dispatchFrameEvents = function (events) {
        this.dispatchEvent(events[0]); //ENTER_FRAME
        this.dispatchEvent(events[1]); //EXIT_FRAME
    };
    DisplayObject.prototype.dispatchEnterFrame = function (event) {
        this.dispatchEvent(event); //ENTER_FRAME
    };
    DisplayObject.prototype.dispatchExitFrame = function (event) {
        this.dispatchEvent(event); //Exit
    };
    DisplayObject.prototype.getMouseCursor = function () {
        return 'initial';
    };
    Object.defineProperty(DisplayObject.prototype, "tabEnabled", {
        get: function () {
            return this._tabEnabled;
        },
        set: function (value) {
            this._tabEnabled = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "tabIndex", {
        get: function () {
            return this._tabIndex;
        },
        set: function (value) {
            this._tabIndex = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "isInFocus", {
        get: function () {
            return this._isInFocus;
        },
        set: function (value) {
            this._isInFocus = value;
        },
        enumerable: true,
        configurable: true
    });
    DisplayObject.prototype.setFocus = function (value, fromMouseDown, sendSoftKeyEvent) {
        if (fromMouseDown === void 0) { fromMouseDown = false; }
        if (sendSoftKeyEvent === void 0) { sendSoftKeyEvent = true; }
        if (this._isInFocus == value)
            return;
        this._isInFocus = value;
        if (this._isInFocus)
            DisplayObject.focusEvent.type = FocusEvent.FOCUS_IN;
        else
            DisplayObject.focusEvent.type = FocusEvent.FOCUS_OUT;
        this.dispatchEnterFrame(DisplayObject.focusEvent);
    };
    Object.defineProperty(DisplayObject.prototype, "alignmentMode", {
        /**
         *
         */
        get: function () {
            return this._alignmentMode;
        },
        set: function (value) {
            if (this._alignmentMode == value)
                return;
            this._alignmentMode = value;
            this._invalidateHierarchicalProperty(HierarchicalProperty.SCENE_TRANSFORM);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "alpha", {
        /**
         * Indicates the alpha transparency value of the object specified. Valid
         * values are 0(fully transparent) to 1(fully opaque). The default value is
         * 1. Display objects with <code>alpha</code> set to 0 <i>are</i> active,
         * even though they are invisible.
         */
        get: function () {
            return this._transform.colorTransform.alphaMultiplier;
        },
        set: function (value) {
            this._transform.colorTransform.alphaMultiplier = value;
            this._transform.invalidateColorTransform();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "blendMode", {
        get: function () {
            if (!Settings.USE_UNSAFE_BLENDS) {
                return '';
            }
            if (Settings.REMAP_BLEND_MODE) {
                return Settings.BLEND_MODE_REMAP_TABLE[this._blendMode] || this._blendMode;
            }
            return this._blendMode;
        },
        set: function (v) {
            if (this._blendMode === v) {
                return;
            }
            this._blendMode = v;
            if (v === BlendMode.OVERLAY) {
                this._transform.invalidateColorTransform();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "defaultBoundingVolume", {
        /**
         *
         */
        get: function () {
            return this._defaultBoundingVolume;
        },
        set: function (value) {
            if (this._defaultBoundingVolume == value)
                return;
            this._defaultBoundingVolume = value;
            if (this._boundsPrimitive) {
                this._boundsPrimitive.dispose();
                this._boundsPrimitive = null;
            }
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "cacheAsBitmap", {
        get: function () {
            return this._bitmapCache;
        },
        set: function (v) {
            if (v === this._bitmapCache) {
                return;
            }
            if (!Settings.USE_UNSAFE_CACHE_AS_BITMAP) {
                return;
            }
            v && console.warn('[@scene/DisplayObject] Unsafe cacheAsBitmap is enabled!' +
                'You can disable it by `Settings.USE_UNSAFE_CACHE_AS_BITMAP = false`', this.id);
            this._bitmapCache = v;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "eulers", {
        /**
         * Defines the rotation of the 3d object as a <code>Vector3D</code>
         * object containing euler angles for rotation around x, y and z axis.
         */
        get: function () {
            if (!this._eulers)
                this._eulers = new Vector3D();
            this._eulers.x = this.rotationX;
            this._eulers.y = this.rotationY;
            this._eulers.z = this.rotationZ;
            return this._eulers;
        },
        set: function (value) {
            // previously this was using the setters for rotationX etc
            // but because this will convert from radians to degree, i changed it to update directly
            this._transform.rotation.x = value.x;
            this._transform.rotation.y = value.y;
            this._transform.rotation.z = value.z;
            this._transform.invalidateMatrix3D();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "filters", {
        get: function () {
            return this._filters;
        },
        set: function (v) {
            if (!Settings.USE_UNSAFE_FILTERS) {
                return;
            }
            this._filters = v;
            this._invalidateStyle();
        },
        enumerable: true,
        configurable: true
    });
    DisplayObject.prototype.updateFilters = function (_newFilters) {
        console.warn('[scene/DisplayObject] - updateFilters is just a stub');
    };
    Object.defineProperty(DisplayObject.prototype, "index", {
        /**
         * Indicates the instance container index of the DisplayObject. The object can be
         * identified in the child list of its parent display object container by
         * calling the <code>getChildByIndex()</code> method of the display object
         * container.
         *
         * <p>If the DisplayObject has no parent container, index defaults to 0.</p>
         */
        get: function () {
            if (this._parent)
                return this._parent.getChildIndex(this);
            return 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "loader", {
        /**
         * Returns a LoaderInfo object containing information about loading the file
         * to which this display object belongs. The <code>loaderInfo</code> property
         * is defined only for the root display object of a SWF file or for a loaded
         * Bitmap(not for a Bitmap that is drawn with ActionScript). To find the
         * <code>loaderInfo</code> object associated with the SWF file that contains
         * a display object named <code>myDisplayObject</code>, use
         * <code>myDisplayObject.root.loaderInfo</code>.
         *
         * <p>A large SWF file can monitor its download by calling
         * <code>this.root.loaderInfo.addEventListener(Event.COMPLETE,
         * func)</code>.</p>
         */
        get: function () {
            return this._loader;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "mask", {
        /**
         * The calling display object is masked by the specified <code>mask</code>
         * object. To ensure that masking works when the Stage is scaled, the
         * <code>mask</code> display object must be in an active part of the display
         * list. The <code>mask</code> object itself is not drawn. Set
         * <code>mask</code> to <code>null</code> to remove the mask.
         *
         * <p>To be able to scale a mask object, it must be on the display list. To
         * be able to drag a mask Sprite object(by calling its
         * <code>startDrag()</code> method), it must be on the display list. To call
         * the <code>startDrag()</code> method for a mask sprite based on a
         * <code>mouseDown</code> event being dispatched by the sprite, set the
         * sprite's <code>buttonMode</code> property to <code>true</code>.</p>
         *
         * <p>When display objects are cached by setting the
         * <code>cacheAsBitmap</code> property to <code>true</code> an the
         * <code>cacheAsBitmapMatrix</code> property to a Matrix object, both the
         * mask and the display object being masked must be part of the same cached
         * bitmap. Thus, if the display object is cached, then the mask must be a
         * child of the display object. If an ancestor of the display object on the
         * display list is cached, then the mask must be a child of that ancestor or
         * one of its descendents. If more than one ancestor of the masked object is
         * cached, then the mask must be a descendent of the cached container closest
         * to the masked object in the display list.</p>
         *
         * <p><b>Note:</b> A single <code>mask</code> object cannot be used to mask
         * more than one calling display object. When the <code>mask</code> is
         * assigned to a second display object, it is removed as the mask of the
         * first object, and that object's <code>mask</code> property becomes
         * <code>null</code>.</p>
         */
        set: function (value) {
            this.scriptMask = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "maskMode", {
        get: function () {
            return this._maskMode;
        },
        set: function (value) {
            if (this._maskMode == value)
                return;
            this._maskMode = value;
            this._maskId = value ? this.id : -1;
            this._updateMaskMode();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "pickObject", {
        get: function () {
            return this._pickObject;
        },
        set: function (value) {
            if (this._pickObject == value)
                return;
            this._pickObject = value.pickObjectFromTimeline ? value.clone() : value;
            if (this._pickObject) {
                this._pickObject.mouseChildren = false;
                this._pickObject.mouseEnabled = false;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "mouseEnabled", {
        /**
         * Specifies whether this object receives mouse, or other user input,
         * messages. The default value is <code>true</code>, which means that by
         * default any InteractiveObject instance that is on the display list
         * receives mouse events or other user input events. If
         * <code>mouseEnabled</code> is set to <code>false</code>, the instance does
         * not receive any mouse events(or other user input events like keyboard
         * events). Any children of this instance on the display list are not
         * affected. To change the <code>mouseEnabled</code> behavior for all
         * children of an object on the display list, use
         * <code>flash.display.DisplayObjectContainer.mouseChildren</code>.
         *
         * <p> No event is dispatched by setting this property. You must use the
         * <code>addEventListener()</code> method to create interactive
         * functionality.</p>
         */
        get: function () {
            return this._mouseEnabled;
        },
        set: function (value) {
            if (this._mouseEnabled == value)
                return;
            this._mouseEnabled = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "mouseX", {
        /**
         * Indicates the x coordinate of the mouse or user input device position, in
         * pixels.
         *
         * <p><b>Note</b>: For a DisplayObject that has been rotated, the returned x
         * coordinate will reflect the non-rotated object.</p>
         */
        get: function () {
            return this._mouseX;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "mouseY", {
        /**
         * Indicates the y coordinate of the mouse or user input device position, in
         * pixels.
         *
         * <p><b>Note</b>: For a DisplayObject that has been rotated, the returned y
         * coordinate will reflect the non-rotated object.</p>
         */
        get: function () {
            return this._mouseY;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "name", {
        /**
         * Indicates the instance name of the DisplayObject. The object can be
         * identified in the child list of its parent display object container by
         * calling the <code>getChildByName()</code> method of the display object
         * container.
         *
         * @throws IllegalOperationError If you are attempting to set this property
         *                               on an object that was placed on the timeline
         *                               in the Flash authoring tool.
         */
        get: function () {
            return this._pName ? this._pName : '';
        },
        set: function (value) {
            this._pName = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "parent", {
        /**
         * Indicates the DisplayObjectContainer object that contains this display
         * object. Use the <code>parent</code> property to specify a relative path to
         * display objects that are above the current display object in the display
         * list hierarchy.
         *
         * <p>You can use <code>parent</code> to move up multiple levels in the
         * display list as in the following:</p>
         *
         * @throws SecurityError The parent display object belongs to a security
         *                       sandbox to which you do not have access. You can
         *                       avoid this situation by having the parent movie call
         *                       the <code>Security.allowDomain()</code> method.
         */
        get: function () {
            return this._parent;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "registrationPoint", {
        /**
         * Defines the local point around which the object rotates.
         */
        get: function () {
            if (this._registrationMatrix3D)
                return new Vector3D(-this._registrationMatrix3D._rawData[12] * this.scaleX, -this._registrationMatrix3D._rawData[13] * this.scaleY, -this._registrationMatrix3D._rawData[14] * this.scaleZ);
            return null;
        },
        set: function (value) {
            if (!value) {
                if (!this._registrationMatrix3D)
                    return;
                this._registrationMatrix3D._rawData[12] = 0;
                this._registrationMatrix3D._rawData[13] = 0;
                this._registrationMatrix3D._rawData[14] = 0;
                if (this._registrationMatrix3D.isIdentity())
                    this._registrationMatrix3D = null;
            }
            else {
                if (!this._registrationMatrix3D)
                    this._registrationMatrix3D = new Matrix3D();
                this._registrationMatrix3D._rawData[12] = -value.x / this._transform.scale.x;
                this._registrationMatrix3D._rawData[13] = -value.y / this._transform.scale.y;
                this._registrationMatrix3D._rawData[14] = -value.z / this._transform.scale.z;
            }
            this._registrationMatrix3D.invalidatePosition();
            this._invalidateHierarchicalProperty(HierarchicalProperty.SCENE_TRANSFORM);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "registrationScale", {
        /**
         * Defines the local scale.
         */
        get: function () {
            if (this._registrationMatrix3D)
                return new Vector3D(this._registrationMatrix3D._rawData[0], this._registrationMatrix3D._rawData[5], this._registrationMatrix3D._rawData[10]);
            return null;
        },
        set: function (value) {
            if (!value) {
                if (!this._registrationMatrix3D)
                    return;
                this._registrationMatrix3D._rawData[0] = 1;
                this._registrationMatrix3D._rawData[5] = 1;
                this._registrationMatrix3D._rawData[10] = 1;
                if (this._registrationMatrix3D.isIdentity())
                    this._registrationMatrix3D = null;
            }
            else {
                if (!this._registrationMatrix3D)
                    this._registrationMatrix3D = new Matrix3D();
                this._registrationMatrix3D._rawData[0] = value.x;
                this._registrationMatrix3D._rawData[5] = value.y;
                this._registrationMatrix3D._rawData[10] = value.z;
            }
            this._invalidateHierarchicalProperty(HierarchicalProperty.SCENE_TRANSFORM);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "root", {
        /**
         * For a display object in a loaded SWF file, the <code>root</code> property
         * is the top-most display object in the portion of the display list's tree
         * structure represented by that SWF file. For a Bitmap object representing a
         * loaded image file, the <code>root</code> property is the Bitmap object
         * itself. For the instance of the main class of the first SWF file loaded,
         * the <code>root</code> property is the display object itself. The
         * <code>root</code> property of the Scene object is the Scene object itself.
         * The <code>root</code> property is set to <code>null</code> for any display
         * object that has not been added to the display list, unless it has been
         * added to a display object container that is off the display list but that
         * is a child of the top-most display object in a loaded SWF file.
         *
         * <p>For example, if you create a new Sprite object by calling the
         * <code>Sprite()</code> constructor method, its <code>root</code> property
         * is <code>null</code> until you add it to the display list(or to a display
         * object container that is off the display list but that is a child of the
         * top-most display object in a SWF file).</p>
         *
         * <p>For a loaded SWF file, even though the Loader object used to load the
         * file may not be on the display list, the top-most display object in the
         * SWF file has its <code>root</code> property set to itself. The Loader
         * object does not have its <code>root</code> property set until it is added
         * as a child of a display object for which the <code>root</code> property is
         * set.</p>
         */
        get: function () {
            return this._root;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "rotationX", {
        /**
         * Indicates the x-axis rotation of the DisplayObject instance, in degrees,
         * from its original orientation relative to the 3D parent container. Values
         * from 0 to 180 represent clockwise rotation; values from 0 to -180
         * represent counterclockwise rotation. Values outside this range are added
         * to or subtracted from 360 to obtain a value within the range.
         */
        get: function () {
            return this._transform.rotation.x * MathConsts.RADIANS_TO_DEGREES;
        },
        set: function (val) {
            if (this.rotationX == val)
                return;
            this._transform.rotation.x = val * MathConsts.DEGREES_TO_RADIANS;
            this._transform.invalidateMatrix3D();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "rotationY", {
        /**
         * Indicates the y-axis rotation of the DisplayObject instance, in degrees,
         * from its original orientation relative to the 3D parent container. Values
         * from 0 to 180 represent clockwise rotation; values from 0 to -180
         * represent counterclockwise rotation. Values outside this range are added
         * to or subtracted from 360 to obtain a value within the range.
         */
        get: function () {
            return this._transform.rotation.y * MathConsts.RADIANS_TO_DEGREES;
        },
        set: function (val) {
            if (this.rotationY == val)
                return;
            this._transform.rotation.y = val * MathConsts.DEGREES_TO_RADIANS;
            this._transform.invalidateMatrix3D();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "rotationZ", {
        /**
         * Indicates the z-axis rotation of the DisplayObject instance, in degrees,
         * from its original orientation relative to the 3D parent container. Values
         * from 0 to 180 represent clockwise rotation; values from 0 to -180
         * represent counterclockwise rotation. Values outside this range are added
         * to or subtracted from 360 to obtain a value within the range.
         */
        get: function () {
            return this._transform.rotation.z * MathConsts.RADIANS_TO_DEGREES;
        },
        set: function (val) {
            if (this.rotationZ == val)
                return;
            this._transform.rotation.z = val * MathConsts.DEGREES_TO_RADIANS;
            this._transform.invalidateMatrix3D();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "scale9Grid", {
        /**
         * The current scaling grid that is in effect. If set to <code>null</code>,
         * the entire display object is scaled normally when any scale transformation
         * is applied.
         *
         * <p>When you define the <code>scale9Grid</code> property, the display
         * object is divided into a grid with nine regions based on the
         * <code>scale9Grid</code> rectangle, which defines the center region of the
         * grid. The eight other regions of the grid are the following areas: </p>
         *
         * <ul>
         *   <li>The upper-left corner outside of the rectangle</li>
         *   <li>The area above the rectangle </li>
         *   <li>The upper-right corner outside of the rectangle</li>
         *   <li>The area to the left of the rectangle</li>
         *   <li>The area to the right of the rectangle</li>
         *   <li>The lower-left corner outside of the rectangle</li>
         *   <li>The area below the rectangle</li>
         *   <li>The lower-right corner outside of the rectangle</li>
         * </ul>
         *
         * <p>You can think of the eight regions outside of the center(defined by
         * the rectangle) as being like a picture frame that has special rules
         * applied to it when scaled.</p>
         *
         * <p>When the <code>scale9Grid</code> property is set and a display object
         * is scaled, all text and gradients are scaled normally; however, for other
         * types of objects the following rules apply:</p>
         *
         * <ul>
         *   <li>Content in the center region is scaled normally. </li>
         *   <li>Content in the corners is not scaled. </li>
         *   <li>Content in the top and bottom regions is scaled horizontally only.
         * Content in the left and right regions is scaled vertically only.</li>
         *   <li>All fills(including bitmaps, video, and gradients) are stretched to
         * fit their shapes.</li>
         * </ul>
         *
         * <p>If a display object is rotated, all subsequent scaling is normal(and
         * the <code>scale9Grid</code> property is ignored).</p>
         *
         * <p>For example, consider the following display object and a rectangle that
         * is applied as the display object's <code>scale9Grid</code>:</p>
         *
         * <p>A common use for setting <code>scale9Grid</code> is to set up a display
         * object to be used as a component, in which edge regions retain the same
         * width when the component is scaled.</p>
         *
         * @throws ArgumentError If you pass an invalid argument to the method.
         */
        get: function () {
            return this._scale9Grid;
        },
        set: function (value) {
            this._scale9Grid = value;
            this._invalidateHierarchicalProperty(HierarchicalProperty.SCALE9);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "scaleX", {
        /**
         * Indicates the horizontal scale(percentage) of the object as applied from
         * the registration point. The default registration point is(0,0). 1.0
         * equals 100% scale.
         *
         * <p>Scaling the local coordinate system changes the <code>x</code> and
         * <code>y</code> property values, which are defined in whole pixels. </p>
         */
        get: function () {
            return this._transform.scale.x;
        },
        set: function (val) {
            //this._updateAbsoluteDimension();
            this._setScaleX(val);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "scaleY", {
        /**
         * Indicates the vertical scale(percentage) of an object as applied from the
         * registration point of the object. The default registration point is(0,0).
         * 1.0 is 100% scale.
         *
         * <p>Scaling the local coordinate system changes the <code>x</code> and
         * <code>y</code> property values, which are defined in whole pixels. </p>
         */
        get: function () {
            return this._transform.scale.y;
        },
        set: function (val) {
            //this._updateAbsoluteDimension();
            this._setScaleY(val);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "scaleZ", {
        /**
         * Indicates the depth scale(percentage) of an object as applied from the
         * registration point of the object. The default registration point is(0,0).
         * 1.0 is 100% scale.
         *
         * <p>Scaling the local coordinate system changes the <code>x</code>,
         * <code>y</code> and <code>z</code> property values, which are defined in
         * whole pixels. </p>
         */
        get: function () {
            return this._transform.scale.z;
        },
        set: function (val) {
            //this._updateAbsoluteDimension();
            this._setScaleZ(val);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "skewX", {
        /**
         * Indicates the horizontal skew(angle) of the object as applied from
         * the registration point. The default registration point is(0,0).
         */
        get: function () {
            return this._transform.skew.x;
        },
        set: function (val) {
            if (this.skewX == val)
                return;
            this._transform.skew.x = val;
            this._transform.invalidateMatrix3D();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "skewY", {
        /**
         * Indicates the vertical skew(angle) of an object as applied from the
         * registration point of the object. The default registration point is(0,0).
         */
        get: function () {
            return this._transform.skew.y;
        },
        set: function (val) {
            if (this.skewY == val)
                return;
            this._transform.skew.y = val;
            this._transform.invalidateMatrix3D();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "skewZ", {
        /**
         * Indicates the depth skew(angle) of an object as applied from the
         * registration point of the object. The default registration point is(0,0).
         */
        get: function () {
            return this._transform.skew.z;
        },
        set: function (val) {
            if (this.skewZ == val)
                return;
            this._transform.skew.z = val;
            this._transform.invalidateMatrix3D();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "scrollRect", {
        /**
         * The scroll rectangle bounds of the display object. The display object is
         * cropped to the size defined by the rectangle, and it scrolls within the
         * rectangle when you change the <code>x</code> and <code>y</code> properties
         * of the <code>scrollRect</code> object.
         *
         * <p>The properties of the <code>scrollRect</code> Rectangle object use the
         * display object's coordinate space and are scaled just like the overall
         * display object. The corner bounds of the cropped window on the scrolling
         * display object are the origin of the display object(0,0) and the point
         * defined by the width and height of the rectangle. They are not centered
         * around the origin, but use the origin to define the upper-left corner of
         * the area. A scrolled display object always scrolls in whole pixel
         * increments. </p>
         *
         * <p>You can scroll an object left and right by setting the <code>x</code>
         * property of the <code>scrollRect</code> Rectangle object. You can scroll
         * an object up and down by setting the <code>y</code> property of the
         * <code>scrollRect</code> Rectangle object. If the display object is rotated
         * 90° and you scroll it left and right, the display object actually scrolls
         * up and down.</p>
         */
        get: function () {
            return this._scrollRect;
        },
        set: function (value) {
            // @todo: check if rectangle has same values as previous
            //if (this._scrollRect == value)
            //	return;
            this._scrollRect = value;
            if (!value && this._scrollRectPrimitive) {
                var idx = this.masks.indexOf(this._scrollRectPrimitive);
                this.masks.splice(idx, 1);
                this._scrollRectPrimitive = null;
            }
            this._transform.invalidatePosition();
            this._scrollRectPrimitiveDirty = true;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "material", {
        /**
         *
         */
        get: function () {
            return this._material;
        },
        set: function (value) {
            if (this._material == value)
                return;
            this._material = value;
            this._invalidateMaterial();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "style", {
        /**
         *
         */
        get: function () {
            return this._style;
        },
        set: function (value) {
            if (this._style == value)
                return;
            if (this._style)
                this._style.removeEventListener(StyleEvent.INVALIDATE_PROPERTIES, this._onInvalidatePropertiesDelegate);
            this._style = value;
            if (this._style)
                this._style.addEventListener(StyleEvent.INVALIDATE_PROPERTIES, this._onInvalidatePropertiesDelegate);
            this._invalidateStyle();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "boundsVisible", {
        /**
         *
         */
        get: function () {
            return this._boundsVisible;
        },
        set: function (value) {
            if (value == this._boundsVisible)
                return;
            this._boundsVisible = value;
            // if (this._boundsVisible && !this.partition)
            // 	this.partition = BasicPartition.getRootPartition(this);
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    DisplayObject.prototype.getScrollRectPrimitive = function () {
        if (this._scrollRectPrimitiveDirty) {
            this._scrollRectPrimitiveDirty = false;
            if (!this._scrollRectPrimitive) {
                this._scrollRectPrimitive = new Sprite();
                this._scrollRectPrimitive.maskMode = true;
                if (!this.masks)
                    this.masks = [];
                this.masks.push(this._scrollRectPrimitive);
            }
            this._scrollRectPrimitive.graphics.clear();
            this._scrollRectPrimitive.graphics.beginFill(0x0000ff, 1);
            this._scrollRectPrimitive.graphics.drawRect(0, 0, this._scrollRect.width, this._scrollRect.height);
            this._scrollRectPrimitive.graphics.endFill();
        }
        return this._scrollRectPrimitive;
    };
    DisplayObject.prototype.getBoundsPrimitive = function (picker) {
        if (this._boundsPrimitive == null) {
            switch (this._defaultBoundingVolume) {
                case BoundingVolumeType.BOX:
                case BoundingVolumeType.BOX_BOUNDS:
                case BoundingVolumeType.BOX_BOUNDS_FAST:
                case BoundingVolumeType.BOX_FAST:
                    this._boundsPrefab = new PrimitiveCubePrefab(null, ElementsType.LINE);
                    this._boundsPrimitive = this._boundsPrefab.getNewObject();
                    break;
                case BoundingVolumeType.SPHERE:
                case BoundingVolumeType.SPHERE_BOUNDS:
                case BoundingVolumeType.SPHERE_BOUNDS_FAST:
                case BoundingVolumeType.SPHERE_FAST:
                    this._boundsPrefab = new PrimitiveSpherePrefab(null, ElementsType.LINE);
                    this._boundsPrimitive = this._boundsPrefab.getNewObject();
                    break;
                default:
            }
            this._boundsPrefabDirty = true;
        }
        if (this._boundsPrefabDirty)
            this._updateBoundsPrefab(picker);
        return this._boundsPrimitive;
    };
    Object.defineProperty(DisplayObject.prototype, "transform", {
        /**
         * An object with properties pertaining to a display object's matrix, color
         * transform, and pixel bounds. The specific properties  -  matrix,
         * colorTransform, and three read-only properties
         * (<code>concatenatedMatrix</code>, <code>concatenatedColorTransform</code>,
         * and <code>pixelBounds</code>)  -  are described in the entry for the
         * Transform class.
         *
         * <p>Each of the transform object's properties is itself an object. This
         * concept is important because the only way to set new values for the matrix
         * or colorTransform objects is to create a new object and copy that object
         * into the transform.matrix or transform.colorTransform property.</p>
         *
         * <p>For example, to increase the <code>tx</code> value of a display
         * object's matrix, you must make a copy of the entire matrix object, then
         * copy the new object into the matrix property of the transform object:</p>
         * <pre xml:space="preserve"><code> public myMatrix:Matrix =
         * myDisplayObject.transform.matrix; myMatrix.tx += 10;
         * myDisplayObject.transform.matrix = myMatrix; </code></pre>
         *
         * <p>You cannot directly set the <code>tx</code> property. The following
         * code has no effect on <code>myDisplayObject</code>: </p>
         * <pre xml:space="preserve"><code> myDisplayObject.transform.matrix.tx +=
         * 10; </code></pre>
         *
         * <p>You can also copy an entire transform object and assign it to another
         * display object's transform property. For example, the following code
         * copies the entire transform object from <code>myOldDisplayObj</code> to
         * <code>myNewDisplayObj</code>:</p>
         * <code>myNewDisplayObj.transform = myOldDisplayObj.transform;</code>
         *
         * <p>The resulting display object, <code>myNewDisplayObj</code>, now has the
         * same values for its matrix, color transform, and pixel bounds as the old
         * display object, <code>myOldDisplayObj</code>.</p>
         *
         * <p>Note that AIR for TV devices use hardware acceleration, if it is
         * available, for color transforms.</p>
         */
        get: function () {
            return this._transform;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "visible", {
        /**
         * Whether or not the display object is visible. Display objects that are not
         * visible are disabled. For example, if <code>visible=false</code> for an
         * InteractiveObject instance, it cannot be clicked.
         */
        get: function () {
            return this._visible;
        },
        set: function (value) {
            if (this._visible == value)
                return;
            this._visible = value;
            this._invalidateHierarchicalProperty(HierarchicalProperty.VISIBLE);
        },
        enumerable: true,
        configurable: true
    });
    DisplayObject.prototype._mergeMasks = function (timeline, script) {
        if (!timeline && !script) {
            return null;
        }
        // we can not have mask array, create it
        if (!timeline && script) {
            script.maskMode = true;
            return [script];
        }
        // and make sure that we not have same script mask in it
        if (script && !timeline.includes(script)) {
            timeline.push(script);
        }
        // and make sure that mask mode enabled
        for (var i = timeline.length - 1; i >= 0; i--) {
            var m = timeline[i];
            if (!m) {
                console.warn('[DisplayObject] Timeline mask has null value, skipping it');
                timeline.splice(i, 1);
            }
            else {
                m.maskMode = true;
            }
        }
        return timeline;
    };
    Object.defineProperty(DisplayObject.prototype, "scriptMask", {
        get: function () {
            return this._scriptMask;
        },
        /**
         *
         * @param mask Masked element, that can be used from script without corruption of timeline mask
         */
        set: function (mask) {
            if (mask === this._scriptMask)
                return;
            // remove older script mask, _mask array for this case can't be null
            if (this._scriptMask) {
                var index = this._masks.indexOf(this._scriptMask);
                if (index !== -1) {
                    this._masks.splice(index, 1);
                }
            }
            this._scriptMask = mask;
            this._masks = this._mergeMasks(this._masks, mask);
            this._invalidateHierarchicalProperty(HierarchicalProperty.MASKS);
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Update mask from timeline and take account that can be a scriptMask
     *
     * @param masks Masks from timeline
     */
    DisplayObject.prototype.updateTimelineMask = function (masks) {
        this._masks = this._mergeMasks(masks, this._scriptMask);
        this._invalidateHierarchicalProperty(HierarchicalProperty.MASKS);
    };
    Object.defineProperty(DisplayObject.prototype, "masks", {
        get: function () {
            return this._masks;
        },
        /**
         * @dangerous Note that we should not use this setter, right way to use `scriptMask` and `updateTimelineMask`
         * @param value
         */
        set: function (value) {
            if (this._masks == value)
                return;
            this._masks = value;
            // this is edge case, if we set masks direct,
            // this means that we should reset _scriptMask when it not present in input mask
            if (!value || !value.length) {
                this._scriptMask = null;
            }
            else {
                var len = value.length;
                var hasScriptMask = false;
                for (var i = 0; i < len; i++) {
                    value[i].maskMode = true;
                    hasScriptMask = hasScriptMask || (value[i] === this._scriptMask);
                }
                if (this._scriptMask && !hasScriptMask) {
                    this._scriptMask = null;
                }
            }
            this._invalidateHierarchicalProperty(HierarchicalProperty.MASKS);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "x", {
        /**
         * Indicates the <i>x</i> coordinate of the DisplayObject instance relative
         * to the local coordinates of the parent DisplayObjectContainer. If the
         * object is inside a DisplayObjectContainer that has transformations, it is
         * in the local coordinate system of the enclosing DisplayObjectContainer.
         * Thus, for a DisplayObjectContainer rotated 90° counterclockwise, the
         * DisplayObjectContainer's children inherit a coordinate system that is
         * rotated 90° counterclockwise. The object's coordinates refer to the
         * registration point position.
         */
        get: function () {
            return this._transform.position.x;
        },
        set: function (val) {
            if (this._transform.position.x == val)
                return;
            this._transform.matrix3D._rawData[12] = val;
            this._transform.invalidatePosition();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "y", {
        /**
         * Indicates the <i>y</i> coordinate of the DisplayObject instance relative
         * to the local coordinates of the parent DisplayObjectContainer. If the
         * object is inside a DisplayObjectContainer that has transformations, it is
         * in the local coordinate system of the enclosing DisplayObjectContainer.
         * Thus, for a DisplayObjectContainer rotated 90° counterclockwise, the
         * DisplayObjectContainer's children inherit a coordinate system that is
         * rotated 90° counterclockwise. The object's coordinates refer to the
         * registration point position.
         */
        get: function () {
            return this._transform.position.y;
        },
        set: function (val) {
            if (this._transform.position.y == val)
                return;
            this._transform.matrix3D._rawData[13] = val;
            this._transform.invalidatePosition();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DisplayObject.prototype, "z", {
        /**
         * Indicates the z coordinate position along the z-axis of the DisplayObject
         * instance relative to the 3D parent container. The z property is used for
         * 3D coordinates, not screen or pixel coordinates.
         *
         * <p>When you set a <code>z</code> property for a display object to
         * something other than the default value of <code>0</code>, a corresponding
         * Matrix3D object is automatically created. for adjusting a display object's
         * position and orientation in three dimensions. When working with the
         * z-axis, the existing behavior of x and y properties changes from screen or
         * pixel coordinates to positions relative to the 3D parent container.</p>
         *
         * <p>For example, a child of the <code>_root</code> at position x = 100, y =
         * 100, z = 200 is not drawn at pixel location(100,100). The child is drawn
         * wherever the 3D projection calculation puts it. The calculation is:</p>
         *
         * <p><code>(x~~cameraFocalLength/cameraRelativeZPosition,
         * y~~cameraFocalLength/cameraRelativeZPosition)</code></p>
         */
        get: function () {
            return this._transform.position.z;
        },
        set: function (val) {
            if (this._transform.position.z == val)
                return;
            this._transform.matrix3D._rawData[14] = val;
            this._transform.invalidatePosition();
        },
        enumerable: true,
        configurable: true
    });
    DisplayObject.prototype.advanceFrame = function () {
        //default to do nothing
    };
    DisplayObject.prototype.isEntity = function () {
        return false;
    };
    /**
     *
     */
    DisplayObject.prototype.clone = function () {
        var newInstance = new DisplayObject();
        this.copyTo(newInstance);
        return newInstance;
    };
    DisplayObject.prototype.copyTo = function (displayObject) {
        var _a;
        displayObject.defaultBoundingVolume = this._defaultBoundingVolume;
        if (this._registrationMatrix3D)
            displayObject._registrationMatrix3D = this._registrationMatrix3D.clone();
        displayObject.material = this._material;
        displayObject.style = this._style;
        displayObject.pickObject = this._pickObject;
        displayObject.pickObjectFromTimeline = this.pickObjectFromTimeline;
        displayObject.boundsVisible = this._boundsVisible;
        displayObject.name = this._pName;
        displayObject.mouseEnabled = this._mouseEnabled;
        displayObject.extra = this.extra;
        displayObject.maskMode = this._maskMode;
        displayObject.castsShadows = this.castsShadows;
        displayObject.assetNamespace = this.assetNamespace;
        displayObject._symbol = this._symbol;
        displayObject.avm1Symbol = this.avm1Symbol;
        displayObject.isAVMScene = this.isAVMScene;
        displayObject.isSlice9ScaledMC = this.isSlice9ScaledMC;
        displayObject.scale9Grid = (_a = this.scale9Grid) === null || _a === void 0 ? void 0 : _a.clone();
        if (this._masks) {
            // clone mask tree, to avoid corruption
            // if _scriptMask is presented, it already was exist in _mask array
            displayObject._masks = this._masks.slice();
            displayObject._scriptMask = this._scriptMask;
            // manually call invalidation
            displayObject._invalidateHierarchicalProperty(HierarchicalProperty.MASKS);
        }
        this._transform.copyRawDataTo(displayObject._transform);
    };
    /**
     *
     */
    DisplayObject.prototype.dispose = function () {
        this.disposeValues();
    };
    DisplayObject.prototype.disposeValues = function () {
        if (this._parent)
            this._parent.removeChild(this);
        this.material = null;
        if (this._adapter) {
            this._adapter.dispose();
            this._adapter = null;
        }
        this._pickObject = null;
        this._masks = null;
        this._scriptMask = null;
    };
    /**
     * Rotates the 3d object around to face a point defined relative
     * to the local coordinates of the parent <code>ObjectContainer3D</code>.
     *
     * @param    scenePosition        The vector defining the point to be looked at
     * @param    upAxis        An optional vector used to define the desired
     * up orientation of the 3d object after rotation has occurred
     */
    DisplayObject.prototype.lookAt = function (scenePosition, upAxis) {
        if (upAxis === void 0) { upAxis = null; }
        this.transform.lookAt(scenePosition, upAxis);
    };
    /**
     * Moves the local point around which the object rotates.
     *
     * @param    dx        The amount of movement along the local x axis.
     * @param    dy        The amount of movement along the local y axis.
     * @param    dz        The amount of movement along the local z axis.
     */
    DisplayObject.prototype.movePivot = function (dx, dy, dz) {
        if (dx == 0 && dy == 0 && dz == 0)
            return;
        if (!this._registrationMatrix3D)
            this._registrationMatrix3D = new Matrix3D();
        this._registrationMatrix3D._rawData[12] -= dx / this._transform.scale.x;
        this._registrationMatrix3D._rawData[13] -= dy / this._transform.scale.y;
        this._registrationMatrix3D._rawData[14] -= dz / this._transform.scale.z;
        this._invalidateHierarchicalProperty(HierarchicalProperty.SCENE_TRANSFORM);
    };
    DisplayObject.prototype.reset = function () {
        this.visible = true;
        if (!this.noTimelineUpdate)
            this._transform.clearMatrix3D();
        this._transform.clearColorTransform();
        //this.name="";
        this.masks = null;
        this.maskMode = false;
    };
    /*
    this checks if the mc is part of the displaylist (child of the stage)
    todo: probably not the best way to do this
    probably better to get this info by looking at root
    */
    DisplayObject.prototype.isOnDisplayList = function () {
        var parent = this;
        while (parent) {
            if (parent.isAVMScene)
                return true;
            parent = parent.parent;
        }
        return false;
    };
    /**
     * @internal
     */
    DisplayObject.prototype._setParent = function (parent) {
        this._parent = parent;
    };
    DisplayObject.prototype._invalidateHierarchicalProperty = function (propDirty) {
        this.dispatchEvent(new HeirarchicalEvent(HeirarchicalEvent.INVALIDATE_PROPERTY, propDirty));
    };
    DisplayObject.prototype._invalidateEntity = function () {
        this.dispatchEvent(new ContainerEvent(ContainerEvent.INVALIDATE_ENTITY, this));
    };
    DisplayObject.prototype._clearEntity = function () {
        this.dispatchEvent(new ContainerEvent(ContainerEvent.CLEAR_ENTITY, this));
    };
    /**
     *
     */
    DisplayObject.prototype._iInternalUpdate = function () {
        if (this._iController)
            this._iController.update();
    };
    Object.defineProperty(DisplayObject.prototype, "maskId", {
        /**
         * @internal
         */
        get: function () {
            return this._maskId;
        },
        enumerable: true,
        configurable: true
    });
    DisplayObject.prototype._acceptTraverser = function (_traverser) { };
    DisplayObject.prototype._setScaleX = function (val) {
        if (this.scaleX == val)
            return;
        this._transform.scale.x = val;
        this._transform.invalidateMatrix3D();
    };
    DisplayObject.prototype._setScaleY = function (val) {
        if (this.scaleY == val)
            return;
        this._transform.scale.y = val;
        this._transform.invalidateMatrix3D();
    };
    DisplayObject.prototype._setScaleZ = function (val) {
        if (this.scaleZ == val)
            return;
        this._transform.scale.z = val;
        this._transform.invalidateMatrix3D();
    };
    DisplayObject.prototype._updateBoundsPrefab = function (picker) {
        if (this._boundsPrefab instanceof PrimitiveCubePrefab) {
            var box = picker.getBoundingVolume(null, this._defaultBoundingVolume).getBox();
            //TODO: if box is null, no prefab should be visible
            if (box == null)
                return;
            this._boundsPrefab.width = box.width;
            this._boundsPrefab.height = box.height;
            this._boundsPrefab.depth = box.depth;
            this._boundsPrimitive.registrationPoint = new Vector3D(-(box.x + box.width / 2) * this._boundsPrimitive.transform.scale.x, -(box.y + box.height / 2) * this._boundsPrimitive.transform.scale.y, -(box.z + box.depth / 2) * this._boundsPrimitive.transform.scale.z);
        }
        else if (this._boundsPrefab instanceof PrimitiveSpherePrefab) {
            var sphere = picker.getBoundingVolume(null, this._defaultBoundingVolume).getSphere();
            //console.log(sphere);
            if (sphere == null)
                return;
            this._boundsPrefab.radius = sphere.radius;
            this._boundsPrimitive.registrationPoint = new Vector3D(-sphere.x * this._boundsPrimitive.transform.scale.x, -sphere.y * this._boundsPrimitive.transform.scale.y, -sphere.z * this._boundsPrimitive.transform.scale.z);
        }
    };
    DisplayObject.prototype._updateMaskMode = function () {
        if (this._maskMode) {
            this._mouseEnabled = false;
            this.partitionClass = BasicPartition;
        }
        this._invalidateHierarchicalProperty(HierarchicalProperty.MASK_ID);
    };
    DisplayObject.prototype._invalidateMaterial = function () {
        this.dispatchEvent(new RenderableEvent(RenderableEvent.INVALIDATE_MATERIAL, this));
    };
    DisplayObject.prototype._invalidateStyle = function () {
        this.dispatchEvent(new RenderableEvent(RenderableEvent.INVALIDATE_STYLE, this));
    };
    DisplayObject.prototype.invalidateElements = function () {
        this.dispatchEvent(new RenderableEvent(RenderableEvent.INVALIDATE_ELEMENTS, this));
    };
    DisplayObject.prototype._onInvalidateProperties = function (_event) {
        if (_event === void 0) { _event = null; }
        this._invalidateStyle();
    };
    DisplayObject.prototype._getDefaultBoundingVolume = function () {
        return BoundingVolumeType.BOX_BOUNDS_FAST;
    };
    DisplayObject.focusEvent = new FocusEvent(FocusEvent.FOCUS_IN);
    return DisplayObject;
}(AssetBase));
export { DisplayObject };
